import React, { useState } from 'react';
import {
  Box, Paper, Typography, TextField, Button, Alert,
  InputAdornment, IconButton, CircularProgress,
} from '@mui/material';
import { Visibility, VisibilityOff, Lock } from '@mui/icons-material';
import { LOGIN_FIELD_LABEL, LOGIN_MESSAGE, LOGIN_TEST_ID, LOGIN_TEXT } from '../constants/auth';
import { TOAST_SEVERITY } from '../constants/toast';
import { getReasonMessage } from '../store/authSlice';
import { useAuthActions, useAuthState } from '../store/authHooks';
import { useToastActions } from '../store/toastHooks';

const Login: React.FC = () => {
  const { loading, logoutReason } = useAuthState();
  const { login } = useAuthActions();
  const { showToast } = useToastActions();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);

  /** Submits trimmed credentials to the auth context and shows a toast on login failure. */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await login(username.trim(), password);
    } catch (err: any) {
      showToast(err?.response?.data?.message || LOGIN_MESSAGE.FAILED, TOAST_SEVERITY.ERROR);
    }
  };

  const reasonMsg = getReasonMessage(logoutReason);

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0077b6 0%, #023e8a 60%, #52b788 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      p: 2,
    }}>
      <Paper sx={{ p: { xs: 3, sm: 5 }, borderRadius: 4, width: '100%', maxWidth: 420, boxShadow: '0 8px 40px rgba(0,0,0,0.2)' }}>
        {/* Brand title */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Box sx={{
            width: 64, height: 64, borderRadius: '50%',
            background: 'linear-gradient(135deg,#0077b6,#52b788)',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', mb: 2,
          }}>
            <Lock sx={{ color: '#fff', fontSize: 32 }} />
          </Box>
          <Typography variant="h5" fontWeight={700} color="primary.dark">{LOGIN_TEXT.BRAND_NAME}</Typography>
          <Typography variant="body2" color="text.secondary">{LOGIN_TEXT.SUBTITLE}</Typography>
        </Box>

        {/* Logout reason banner */}
        {reasonMsg && (
          <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>{reasonMsg}</Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            fullWidth label={LOGIN_FIELD_LABEL.USERNAME} size="small" sx={{ mb: 2 }}
            value={username}
            onChange={e => setUsername(e.target.value)}
            autoComplete="username"
            required
            inputProps={{ 'data-testid': LOGIN_TEST_ID.USERNAME }}
          />
          <TextField
            fullWidth label={LOGIN_FIELD_LABEL.PASSWORD} size="small" sx={{ mb: 3 }}
            type={showPwd ? 'text' : 'password'}
            value={password}
            onChange={e => setPassword(e.target.value)}
            autoComplete="current-password"
            required
            inputProps={{ 'data-testid': LOGIN_TEST_ID.PASSWORD }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    aria-label={showPwd ? LOGIN_TEXT.HIDE_PASSWORD : LOGIN_TEXT.SHOW_PASSWORD}
                    onClick={() => setShowPwd(v => !v)}
                    edge="end"
                  >
                    {showPwd ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Button
            type="submit" variant="contained" fullWidth size="large"
            disabled={loading}
            sx={{ borderRadius: 2, py: 1.2, fontWeight: 700 }}
          >
            {loading ? <CircularProgress size={22} color="inherit" /> : LOGIN_TEXT.SUBMIT_BUTTON}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default Login;
